import { useCallback, useMemo, useState } from "react";
import { PencilIcon, ChevronDownIcon, ClockIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { VSCodeDropdown, VSCodeOption } from "@vscode/webview-ui-toolkit/react";
import { gql, useQuery } from "@apollo/client";
import clsx from "clsx";

import ErrorGrow from "@/components/lib/ErrorGrow";
import ExpandCenter from "@/components/lib/ExpandCenter";
import Loading from "@/components/lib/Loading";
import compact from "lodash/compact";
import moment from "moment";
import orderBy from "lodash/orderBy";
import isEqual from "lodash/isEqual";

moment.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "seconds",
    ss: "%ss",
    m: "a minute",
    mm: "%dm",
    h: "an hour",
    hh: "%dh",
    d: "a day",
    dd: "%dd",
    M: "a month",
    MM: "%dM",
    y: "a year",
    yy: "%dY",
  },
});

const ExpandButton = ({ isExpanded, setIsExpanded, label = null, classes = "w-4" }) => {
  const ChevronClasses = `group-hover:text-covey-500 ${classes}`;

  const handleOpen = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded, setIsExpanded]);

  return (
    <div
      onClick={handleOpen}
      className={clsx("cursor-pointer group hover:text-covey-500 text-foreground", {
        "flex flex-row space-x-1 items-center": label !== null,
      })}
    >
      {label !== null && <div className="text-xs font-medium">{label}</div>}
      <ChevronUpIcon
        className={clsx(ChevronClasses, {
          hidden: !isExpanded,
          block: isExpanded,
        })}
      />
      <ChevronDownIcon
        className={clsx(ChevronClasses, {
          hidden: isExpanded,
          block: !isExpanded,
        })}
      />
    </div>
  );
};
const OpenProjectButton = ({ href, text = "Open in VS Code" }) => (
  <a href={href}>
    <div className="max-w-[165px] border flex-row-centered px-2 py-0.5 whitespace-nowrap space-x-2 rounded shadow">
      <div className="h-4 w-4">
        <img src="/assets/vscode.png" alt="VS Code" />
      </div>
      <div>Open in VS Code</div>
    </div>
  </a>
);

const GET_VSCODE_SESSIONS = gql`
  query GetVSCodeSessions {
    user {
      id
      sessions(sinceDays: 7) {
        nextPageToken
        data {
          device {
            appHost
            id
            platform
            platformVersion
            remoteName
          }
          startTime
          endTime
          sessionIds
          projects {
            data {
              baseProjectId
              displayName
              folderUri
              gitUrl
              name
              shortName
              url
              codingDuration {
                readingSeconds
                totalSeconds
                writingSeconds
              }
              id
              workspaceId
              branches
            }
          }
          workspaces {
            data {
              id
              folderUri
              name
            }
          }
        }
      }
      annotations {
        data {
          id
          project {
            id
            name
            shortName
            url
            displayName
            gitUrl
          }
        }
      }
    }
  }
`;

function convertToReadable(seconds) {
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 1 && minutes < 60) {
    return `${minutes} m`;
  }
  if (minutes >= 60) {
    let hours = (minutes / 60).toFixed(0);
    let remainingMinues = minutes % 60;
    return `${hours} h ${remainingMinues} m`;
  }
  return "0 m";
}

//show codespace or folder uri or none
// todo when we have access to codespace url from gql
const showFolderButton = (device, project) => {
  if (device.remoteName === null && project.folderUri) {
    return <OpenProjectButton href={`vscode://file/${project.folderUri?.substring(7)}`} />;
  } else if (device.remoteName === "codespaces") {
    return <OpenProjectButton href={"/"} text="Open in Codespaces" />;
  } else {
    return <div className="whitespace-nowrap">No Folder URI</div>;
  }
};

const ProjectContainer = ({ project, partOfWorkspace = true, subProjects, device }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const readableDuration = convertToReadable(project?.codingDuration?.totalSeconds);
  if (!project || !project.displayName) return;
  return (
    <div className={"w-full flex flex-col lg:flex-row lg:items-start"}>
      <div className="flex flex-col flex-grow w-full">
        <div className="w-full flex justify-between space-x-3 lg:justify-start flex-wrap">
          <div className="flex flex-col flex-grow">
            <div className="font-semibold text-covey-500 max-w-[500px] flex-row-centered  flex-wrap">
              <div className="text-covey-500">
                {project?.displayName}
                {project?.branches !== null && `#${project?.branches[0]}`}
                {project?.branches?.length > 1 && (
                  <ExpandButton
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    label={"Other branches"}
                  />
                )}
                {project?.branches?.length > 1 &&
                  isExpanded &&
                  project?.branches?.slice(1).map((branch, i) => {
                    return (
                      <div key={i} className="pl-3">
                        #{branch}
                      </div>
                    );
                  })}
              </div>
            </div>

            {subProjects &&
              subProjects?.map((project, i) => {
                return (
                  <div className="pl-5 group flex flex-row-centered font-semibold" key={i}>
                    <div>
                      {project?.displayName}
                      {project?.branches !== null && `#${project?.branches[0]}`}
                    </div>
                  </div>
                );
              })}
          </div>
          {partOfWorkspace && (
            <div className="flex justify-end items-start space-x-4">
              {project?.annotations.length > 0 && (
                <div className="flex items-center">
                  <PencilIcon className="w-5" />
                  {project?.annotations?.length}
                </div>
              )}
              {project?.codingDuration !== null && (
                <div className="flex items-center">
                  <ClockIcon className="w-5 mr-1" />
                  {readableDuration}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col lg:items-end">
        {!partOfWorkspace && (
          <div className="flex space-x-4 lg:justify-end">
            {project?.annotations?.length > 0 && (
              <div className="flex items-center whitespace-nowrap">
                <PencilIcon className="w-5" />
                {project?.annotations?.length}
              </div>
            )}
            {project?.codingDuration !== null && (
              <div className="flex items-center whitespace-nowrap">
                <ClockIcon className="w-5 mr-1" />
                {readableDuration}
              </div>
            )}
            {showFolderButton(device, project)}
            {/* {device.remoteName === null && project.folderUri ? (
              <>
                <OpenProjectButton href={`vscode://file/${project.folderUri?.substring(7)}`} />
              </>
            ) : (
              <div className="whitespace-nowrap">No Folder URI</div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

const WorkspaceContainer = ({ workspace }) => {
  const workspaceProjects = useMemo(() => compact(workspace?.projects), [workspace?.projects]);
  const [isExpanded, setIsExpanded] = useState(false);

  const projects = useMemo(() => {
    if (isExpanded) {
      return workspaceProjects;
    } else {
      return workspaceProjects.slice(0, 2);
    }
  }, [workspaceProjects, isExpanded]);

  const label = useMemo(() => {
    if (isExpanded) {
      return `Less files`;
    } else {
      return `More files (${workspaceProjects.length - projects.length})`;
    }
  }, [isExpanded, workspaceProjects, projects]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col justify-between w-full lg:flex-row">
        <div className="w-full flex flex-col lg:flex-row justify-between">
          <div className="font-semibold flex-row-centered flex-grow">
            <div className="text-blue-header">{workspace.name}</div>
          </div>
          <OpenProjectButton href={`vscode://file/${workspace?.folderUri?.substring(7)}`} />
        </div>
      </div>
      {workspaceProjects?.length > 0 && (
        <div className="w-full">
          <div className={`pl-2 flex ${!isExpanded && "items-center"} space-x-2 `}>
            <div className="flex-grow">
              {workspaceProjects?.map((project, i) => {
                return (
                  <ProjectContainer key={i} project={project} subProjects={project?.subProjects} />
                );
              })}
              {workspaceProjects?.length > 3 && (
                <div className="flex flex-shrink pt-1">
                  <ExpandButton
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    label={label}
                    classes="w-3"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function convertMinsToHours(duration) {
  if (duration.asMinutes() > 60) {
    return `${duration.asHours().toFixed(1)} hours`;
  } else {
    return `${duration.asMinutes().toFixed(0)} minutes`;
  }
}
const SessionContainer = ({ session }) => {
  const compactProjects = useMemo(() => compact(session?.projects?.data), [session]);
  const startTime = moment(session.startTime);
  const endTime = moment(session.endTime);
  const duration = moment.duration(endTime.diff(startTime));
  const sessionDuration = convertMinsToHours(duration);

  const device = session.device;
  if (session.projects.data?.length < 1 && session.workspaces.data?.length < 1) {
    return;
  }
  // if this is true this will just show an empty session row which doesn't make sense to do. But there is session details which persisted it after flattening
  // edge-case when there is a workspaceId reference but session recognizes no workspace
  if (session.projects.data[0] === undefined && session.workspaces.data.length === 0) {
    return;
  }
  return (
    <div className="py-4 border-b">
      <div className="flex flex-col w-full lg:flex-row lg:items-start select-none cursor-default">
        <div className="w-16 font-medium">
          {isEqual(device?.platform, "linux") && isEqual(device?.remoteName, null) && (
            <div className="max-w-[30px] max-h-[30px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30px"
                height="30px"
                viewBox="0 0 30 30"
                role="img"
                fill="currentColor"
              >
                <title>Linux icon</title>
                <path
                  fill="currentColor"
                  d="M12.503 0c-.155 0-.315.008-.479.021-4.227.333-3.106 4.807-3.17 6.298-.077 1.093-.3 1.954-1.051 3.021-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489-.039.034-.076.068-.111.135-.26.268-.449.601-.662.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.681-.09.188-.136.393-.132.601 0 .199.027.401.055.536.058.399.116.729.039.97-.248.68-.279 1.146-.105 1.485.174.334.535.469.939.601.811.2 1.91.135 2.775.599.926.467 1.866.671 2.616.47.526-.115.97-.463 1.208-.945.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.578.199.025.135.063.199.114.334l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.199-.812-.714-1.377v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135-.056-.038-.121-.062-.19-.064.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.561-3.369.027-2.152.237-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.191.135.33.332.439.533.105.259.158.459.166.724 0-.02.006-.04.006-.059v.104c-.002-.007-.004-.015-.004-.021l-.004-.024c-.002.243-.053.483-.15.706-.047.125-.119.24-.213.335-.029-.016-.057-.03-.088-.042-.105-.045-.199-.064-.285-.133-.071-.028-.145-.051-.219-.066.049-.059.145-.133.182-.198.053-.128.082-.264.088-.402v-.019c.002-.135-.018-.271-.061-.4-.045-.135-.101-.201-.183-.334-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132-.095.094-.166.207-.205.334-.053.127-.084.264-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202-.01-.065-.016-.132-.018-.199v-.02c-.008-.264.043-.526.15-.769.082-.219.232-.406.43-.533.171-.129.379-.199.594-.199zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.141.401.153.667v.004c.007.134.006.201-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.089.013-.179.003-.267v-.015c-.012-.133-.039-.199-.082-.333-.031-.102-.088-.193-.166-.267-.049-.045-.115-.068-.183-.064h-.021c-.071.006-.13.041-.186.132-.064.077-.105.171-.12.27-.027.108-.035.219-.023.331v.014c.012.135.037.201.081.334.045.134.097.2.165.268.011.009.021.018.034.024-.07.057-.117.07-.176.136-.037.028-.08.06-.131.068-.104-.125-.196-.26-.275-.402-.096-.21-.146-.438-.155-.667-.017-.226.011-.452.08-.668.055-.197.152-.379.283-.535.128-.133.26-.2.418-.2zm1.371 1.706c.331 0 .732.065 1.215.399.293.2.523.269 1.053.468h.003c.255.136.405.266.478.399v-.131c.073.147.078.318.016.47-.123.311-.516.644-1.064.843v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267-.152.008-.305-.015-.448-.067-.11-.06-.218-.126-.322-.198-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.711-.069-.268-.005-.469.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.469.839-.601.139-.036.294-.065.466-.065zm2.799 2.143c.359 1.417 1.197 3.475 1.736 4.473.286.534.855 1.659 1.102 3.024.156-.005.329.018.513.064.646-1.671-.546-3.467-1.089-3.966-.221-.201-.232-.335-.123-.335.589.534 1.365 1.572 1.646 2.757.129.535.159 1.104.021 1.67.067.028.135.061.205.067 1.032.534 1.413.938 1.23 1.537v-.043c-.061-.003-.12 0-.181 0h-.015c.151-.467-.182-.825-1.065-1.224-.915-.4-1.646-.336-1.771.465-.007.043-.012.066-.017.135-.068.023-.139.053-.209.064-.43.268-.662.669-.793 1.187-.13.533-.17 1.156-.205 1.869v.003c-.021.334-.171.838-.319 1.351-1.5 1.072-3.58 1.538-5.349.334-.111-.193-.246-.374-.402-.533-.074-.125-.168-.237-.275-.334.182 0 .338-.029.465-.067.145-.067.257-.188.314-.334.108-.267 0-.697-.345-1.163-.345-.467-.931-.995-1.788-1.521-.63-.399-.986-.87-1.15-1.396-.165-.534-.143-1.085-.015-1.645.245-1.07.873-2.11 1.274-2.763.107-.065.037.135-.408.974-.396.751-1.141 2.497-.122 3.854.038-.989.258-1.965.647-2.876.564-1.278 1.743-3.504 1.836-5.268.048.036.217.135.289.202.218.133.38.333.59.465.211.201.477.335.876.335.039.003.075.006.111.006.411 0 .729-.134.996-.268.29-.134.521-.334.74-.4h.005c.467-.135.836-.402 1.045-.7zm2.186 8.958c.037.601.343 1.245.882 1.377.588.134 1.434-.333 1.791-.765l.211-.009c.315-.008.577.01.847.267l.003.003c.208.199.305.531.391.876.085.401.154.78.409 1.066.486.527.645.906.636 1.14l.003-.006v.018l-.003-.012c-.015.262-.185.396-.498.595-.63.401-1.746.712-2.457 1.57-.618.737-1.371 1.139-2.036 1.191-.664.053-1.237-.2-1.574-.898l-.005-.003c-.21-.401-.12-1.025.056-1.69.176-.668.428-1.345.463-1.898.037-.714.076-1.335.195-1.814.12-.465.308-.797.641-.984l.045-.022zm-10.815.049h.01c.053 0 .105.005.157.014.376.055.706.333 1.023.752l.91 1.664.003.003c.243.533.754 1.064 1.189 1.638.434.598.77 1.131.729 1.57v.006c-.057.744-.479 1.148-1.125 1.294-.645.135-1.52.002-2.395-.464-.968-.536-2.118-.469-2.857-.602-.369-.066-.611-.201-.723-.401-.111-.199-.113-.601.123-1.23v-.003l.002-.003c.117-.334.03-.753-.027-1.119-.055-.401-.083-.709.043-.94.16-.334.396-.399.689-.533.295-.135.641-.202.916-.469h.002v-.003c.256-.268.445-.601.668-.838.19-.201.38-.336.663-.336zm7.159-9.074c-.435.201-.945.535-1.488.535-.542 0-.97-.267-1.279-.466-.155-.134-.28-.268-.374-.335-.164-.134-.144-.334-.074-.334.109.016.129.135.199.201.096.066.215.199.361.333.291.2.68.467 1.166.467.485 0 1.053-.267 1.398-.466.195-.135.445-.334.648-.467.156-.137.149-.268.279-.268.129.016.034.134-.147.333-.181.135-.461.335-.69.468v-.001zm-1.082-1.584V5.64c-.006-.019.013-.042.029-.05.074-.043.18-.027.26.004.063 0 .16.067.15.135-.006.049-.085.066-.135.066-.055 0-.092-.043-.141-.068-.052-.018-.146-.008-.163-.065zm-.551 0c-.02.058-.113.049-.166.066-.047.025-.086.068-.139.068-.051 0-.131-.019-.137-.068-.009-.066.088-.133.15-.133.081-.031.184-.047.259-.005.019.009.036.03.03.05v.021h.003z"
                />
              </svg>
            </div>
          )}

          {isEqual(device?.platform, "darwin") && (
            <svg viewBox="0 0 170 170" fill="currentColor" className="max-w-[30px] max-h-[30px]">
              <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71.12 1.083.17 2.166.17 3.24z" />
            </svg>
          )}
          {isEqual(device?.remoteName, "codespaces") && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M9 19c-4.3 1.4 -4.3 -2.5 -6 -3m12 5v-3.5c0 -1 .1 -1.4 -.5 -2c2.8 -.3 5.5 -1.4 5.5 -6a4.6 4.6 0 0 0 -1.3 -3.2a4.2 4.2 0 0 0 -.1 -3.2s-1.1 -.3 -3.5 1.3a12.3 12.3 0 0 0 -6.2 0c-2.4 -1.6 -3.5 -1.3 -3.5 -1.3a4.2 4.2 0 0 0 -.1 3.2a4.6 4.6 0 0 0 -1.3 3.2c0 4.6 2.7 5.7 5.5 6c-.6 .6 -.6 1.2 -.5 2v3.5"></path>
            </svg>
          )}

          {isEqual(device?.platform, "win32") && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="73"
              height="66"
              viewBox="0 0 73 66"
              fill="none"
              className="max-w-[30px] max-h-[30px]"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M57.57 1.12163C53.17 1.81863 46.983 2.70962 43.82 3.10062L38.07 3.81162V16.7396V29.6676H55.097H72.123L71.847 14.9176L71.57 0.167625L68.57 0.00962504C66.92 -0.076375 61.97 0.423626 57.57 1.12163ZM15.07 7.01463L0.57 9.16763L0.285 19.4176L0 29.6676H16.035H32.07V17.1676C32.07 7.78263 31.759 4.69163 30.82 4.76463C30.133 4.81763 23.045 5.83063 15.07 7.01463ZM0.285 45.9176L0.57 56.1676L12.07 57.8576C18.395 58.7866 25.483 59.8266 27.82 60.1686L32.07 60.7906V48.2286V35.6676H16.035H0L0.285 45.9176ZM38.07 48.6676C38.07 56.7346 38.452 61.6676 39.078 61.6676C39.632 61.6676 46.669 62.5916 54.716 63.7216C62.762 64.8516 69.959 65.5406 70.708 65.2526C71.771 64.8456 72.07 61.5386 72.07 50.1986V35.6676H55.07H38.07V48.6676Z"
                fill="currentColor"
              />
            </svg>
          )}
        </div>
        <div className="flex-grow mt-4 lg:w-1/2 lg:mt-0">
          {session?.workspaces?.data.length > 0 && (
            <div>
              <div className="flex items-center justify-between"></div>
              <div className="space-y-4">
                {session.workspaces.data.map((workspace) => {
                  return (
                    <WorkspaceContainer
                      key={session.startTime + "_" + workspace?.name}
                      workspace={workspace}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {session?.projects?.data.length > 0 && (
            <div className="w-full">
              <div className={`space-y-4 ${session.workspaces.data.length > 0 && "mt-2"}`}>
                {compactProjects.map((project, index) => {
                  return (
                    <ProjectContainer
                      key={session.startTime + "_" + project?.displayName + index}
                      project={project}
                      sessionDuration={sessionDuration}
                      partOfWorkspace={false}
                      subProjects={project?.subProjects}
                      device={device}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 lg:pl-[4rem] space-x-12">
        <div className="">
          <span className="font-medium">Last active: </span>
          {moment(session.endTime).fromNow(true)} ago
        </div>
      </div>
    </div>
  );
};

const TimelineContainer = ({ timeline }) => {
  return (
    <div className="flex flex-col py-4 space-y-3">
      {timeline.map((session, index) => {
        return <SessionContainer key={`${session.startTime}-${index}`} session={session} />;
      })}
    </div>
  );
};

const DashGrid = ({ timeline, setFilterBy }) => {
  return (
    <div className="flex flex-col py-4">
      <div className="flex flex-row items-center justify-between pb-2 border-b">
        <VSCodeDropdown onChange={(e) => setFilterBy(e.target.value)}>
          <VSCodeOption>All</VSCodeOption>
          {/* <VSCodeOption>Branch</VSCodeOption> */}
          {/* <VSCodeOption>Instance</VSCodeOption> */}
          <VSCodeOption>Workspace</VSCodeOption>
          <VSCodeOption>Project</VSCodeOption>
        </VSCodeDropdown>
        <VSCodeDropdown>
          <VSCodeOption>Activity</VSCodeOption>
          {/* <VSCodeOption>Artifacts</VSCodeOption> */}
        </VSCodeDropdown>
      </div>
      <div>
        <TimelineContainer timeline={timeline} />
      </div>
    </div>
  );
};

function flattenWorkspaceAndOrder(workspace) {
  const byWorkspaceFlat = compact(workspace);
  return orderBy(byWorkspaceFlat, ["startTime"], ["desc"]);
}

const DashContainer = () => {
  const [filterBy, setFilterBy] = useState("All");
  const { loading, error, data } = useQuery(GET_VSCODE_SESSIONS);

  const getAnnotationsForProject = useCallback(
    (p) => {
      return data?.user?.annotations?.data.filter(({ project }) => project?.id === p?.id);
    },
    [data]
  );

  const sessions = useMemo(() => {
    if (data) {
      const merged = data.user?.sessions?.data.map((item, i) => {
        if (item.workspaces.data.length > 0) {
          return {
            ...item,
            projects: {
              ...item.projects,
              data: item.projects?.data.map((p) => {
                if (p.workspaceId !== null) {
                  return undefined;
                }
                return p;
              }),
            },
            workspaces: {
              ...item.workspaces,
              data: item.workspaces?.data.map((workspace) => {
                let baseProject;
                let subProjects = [];
                return {
                  ...workspace,
                  projects: item.projects?.data.map((p) => {
                    const annotations = getAnnotationsForProject(p);
                    const foundBaseProject = item.projects?.data.find(
                      (pr) => pr.id === p.baseProjectId
                    );
                    // if base project exists we need to remove from the project array and append to the baseProject subProjects
                    if (foundBaseProject && foundBaseProject.id !== p.id) {
                      baseProject = foundBaseProject;
                      subProjects.push(p);
                      return undefined;
                    }
                    if (baseProject?.id === p.id) {
                      return {
                        ...p,
                        subProjects,
                        annotations,
                      };
                    }
                    if (p?.workspaceId === workspace.id) {
                      return {
                        ...p,
                        annotations,
                      };
                    }
                    return undefined;
                  }),
                };
              }),
            },
          };
        }
        if (item.projects.data.length > 0) {
          let baseProject;
          let subProjects = [];
          // todo: LJ find a way not to DRY this up:
          return {
            ...item,
            projects: {
              ...item.projects,
              data: item.projects.data.map((p) => {
                if (p.workspaceId !== null && p.baseProjectId === null) {
                  return undefined;
                }
                const annotations = getAnnotationsForProject(p);
                const foundBaseProject = item.projects?.data.find(
                  (pr) => pr.id === p.baseProjectId
                );
                //handles if baseProjectId === project.id itself (edge-case)
                if (foundBaseProject && foundBaseProject.id !== p.id) {
                  baseProject = foundBaseProject;
                  subProjects.push(p);
                  return undefined;
                }
                if (baseProject?.id === p.id) {
                  return {
                    ...p,
                    subProjects,
                    annotations,
                  };
                }
                return {
                  ...p,
                  annotations,
                };
              }),
            },
          };
        }
        return {
          ...item,
        };
      });
      return merged;
    }
  }, [data, getAnnotationsForProject]);

  const filteredTimeline = useMemo(() => {
    if (sessions && filterBy === "Workspace") {
      const byWorkspace = sessions.map((session) => {
        if (session?.workspaces?.data?.length > 0) {
          return session;
        }
        return undefined;
      });
      return flattenWorkspaceAndOrder(byWorkspace);
    }
    if (sessions && filterBy === "Project") {
      const byWorkspace = sessions.map((session) => {
        if (session?.workspaces?.data?.length === 0) {
          return session;
        }
        return undefined;
      });
      return flattenWorkspaceAndOrder(byWorkspace);
    }
  }, [sessions, filterBy]);

  const timeline = useMemo(() => {
    return orderBy(sessions, ["startTime"], ["desc"]);
  }, [sessions]);

  if (loading) {
    return (
      <ExpandCenter>
        <Loading />
      </ExpandCenter>
    );
  }

  if (error) {
    <ErrorGrow message={error.message} />;
  }
  if (filterBy === "Workspace") {
    return <DashGrid timeline={filteredTimeline} filterBy={filterBy} setFilterBy={setFilterBy} />;
  }
  if (filterBy === "Project") {
    return <DashGrid timeline={filteredTimeline} filterBy={filterBy} setFilterBy={setFilterBy} />;
  }
  return <DashGrid timeline={timeline} filterBy={filterBy} setFilterBy={setFilterBy} />;
};

export default DashContainer;
